<template>
  <div class="LocationGoogleMap" aria-hidden="true" ref="mapcontainer">
    <!--Map and infobox-->
    <div class="map-wrapper">
      <!--Map-->
      <vue-map
        :class="['map', { ready }]"
        :options="mapSettings.config"
        :center="getCenter"
        :zoom="zoom"
        ref="map"
        @click="closeInfoWindow()"
      >
        <vue-cluster :zoomOnClick="true" :styles="clusterSettings">
          <vue-marker
            v-for="(marker, idx) in markers"
            :key="`marker-${idx}-${_uid}`"
            :position="marker.position"
            :options="
              openedInfoMarker === marker
                ? selectedMarkerSettings
                : markerSettings
            "
            :ref="`marker-${idx}-${_uid}`"
            :clickable="true"
            @click="markerClickHandler(marker)"
          ></vue-marker>
        </vue-cluster>
        <!--INFOBOX ON THE SIDE DESKTOP-->
        <transition name="fade" mode="out-in">
          <template v-if="!mobileMatches() && openedInfoMarker">
            <vue-info-window
              :opened="openedInfoMarker != null"
              :options="getInfoWindowOptions"
              :position="getInfoWindowPosition"
              @closeclick="openedInfoMarker = null"
            >
              <map-info-box
                :openedInfoMarker="openedInfoMarker"
                boxclass="infobox"
                :labels="labels"
              ></map-info-box>
            </vue-info-window>
          </template>
        </transition>
      </vue-map>
      <!--INFOBOX MODAL MOBILE-->
      <!--mo-->
      <template v-if="mobileMatches()">
        <div ref="modal" class="modal" @click.prevent="closeInfoWindow()">
          <span class="close">&times;</span>
          <map-info-box
            :openedInfoMarker="openedInfoMarker"
            boxclass="modalbox"
          ></map-info-box>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import * as VueGoogleMaps from 'vue2-google-maps';
import GmapCluster from 'vue2-google-maps/src/components/cluster';
import GmapInfoWindow from 'vue2-google-maps/src/components/infoWindow';
import { getMapSettings } from '@helpers/map-config';
import MapInfoBox from '@components/map-info-box';
import _debounce from 'lodash.debounce';

export default {
  name: 'LocationsGoogleMap',
  components: {
    VueMap: VueGoogleMaps.Map,
    VueMarker: VueGoogleMaps.Marker,
    VueCluster: GmapCluster,
    VueInfoWindow: GmapInfoWindow,
    MapInfoBox
  },
  props: {
    mapStyles: {type: String},
    center: {type: Object},
    zoom: {type: Number},
    markers: {type: Array},
    refreshPayload: {type: String},
    labels: {type: Object}
  },
  data() {
    return {
      scroller: null,
      scrollerIndex: 0,
      ready: false,
      map: undefined,
      gMapsApi: undefined,
      openedInfoMarker: null,
      storedCenter: null
    };
  },
  watch: {
    refreshPayload() {
      this.openedInfoMarker = null;
    }
  },
  computed: {
    // center change when a marker is selected
    // we store the center position in order to keep that when the marker info window is closed
    getCenter() {
      var newCenter = this.openedInfoMarker
        ? {
          lat: parseFloat(this.openedInfoMarker.position.lat),
          lng: parseFloat(this.openedInfoMarker.position.lng)
        }
        : this.center;
      //     : (this.storedCenter
      //         ? this.storedCenter
      //         : this.center);
      this.storedCenter = newCenter;
      return newCenter;
    },
    // the infoWindow position only for latitude and longitute
    getInfoWindowPosition() {
      return {
        lat: parseFloat(this.openedInfoMarker.position.lat),
        lng: parseFloat(this.openedInfoMarker.position.lng)
      };
    },
    // the offset from the marker
    // others top/right margins are achived with css
    getInfoWindowOptions() {
      return {pixelOffset: '50'};
    },
    // marker position, fallback on 0,0
    getPosition() {
      return this.openedInfoMarker
        ? {
          lat: parseFloat(this.openedInfoMarker.position.lat),
          lng: parseFloat(this.openedInfoMarker.position.lng)
        }
        : {lat: 0, lng: 0};
    },
    // wysiwyg content with fallback
    getContent() {
      return this.openedInfoMarker ? this.openedInfoMarker.address : '';
    },
    // settings from map-config.js
    mapSettings() {
      var mapconf = getMapSettings(this.mapStyles);
      mapconf.config.zoom = this.zoom;
      return mapconf;
    },
    markerSettings() {
      return {
        icon: {
          ...this.mapSettings.marker.icon,
          url: `${this.$context['assetsRoot']}img/${this.mapSettings.marker.icon.url}`
        },
        label: {
          ...this.mapSettings.marker.label
        }
      };
    },
    selectedMarkerSettings() {
      return {
        icon: {
          ...this.mapSettings.selected.icon,
          url: `${this.$context['assetsRoot']}img/${this.mapSettings.selected.icon.url}`
        },
        label: {
          ...this.mapSettings.selected.label
        }
      };
    },
    clusterSettings() {
      return [
        {
          icon: {
            labelOrigin: {x: 27, y: 20}
          },
          height: 53,
          url: `${this.$context['assetsRoot']}img/barilla-drop-marker.svg`,
          width: 53,
          textColor: 'white',
          textDecoration: 'none',
          textSize: 20
        }
      ];
    }
  },
  methods: {
    resetScroller: _debounce((scope) => {
      scope.scroller.style.zIndex = scope.scrollerIndex;
    }, 2000),
    scrollHandler() {
      if (!this.scroller) {
        this.scroller = document.getElementsByClassName('gm-style-pbc')[0];
        this.scrollerIndex = this.scroller.style.zIndex;
      }
      this.scroller.style.zIndex = `${+this.scrollerIndex + 10}`;
      this.resetScroller(this);
    },
    mobileMatches() {
      return this.$viewportWidth < 768;
    },
    markerClickHandler(marker) {
      this.openedInfoMarker = marker;
      if (this.$refs.modal) {
        this.$refs.modal.style.display = 'block';
      }
    },
    closeInfoWindow() {
      this.openedInfoMarker = null;
      if (this.$refs.modal) {
        this.$refs.modal.style.display = 'none';
      }
    },
    mapClickHandler($evt) {
      this.openedInfoMarker = null;
      // console.log($evt);
      // TODO map click handler: currently noop
    },
    markerMouseoverHandler($evt) {
      // console.log($evt);
      // TODO map mouseover handler: currently noop
    },
    markerMouseoutHandler($evt) {
      // console.log($evt);
      // TODO map mouseout handler: currently noop
    }
  },
  created() {
    // apikey is stored in settings.py
    Vue.use(VueGoogleMaps, {
      load: {key: this.$context['apiKeys']['google_maps']},
      installComponents: false
    });
  },
  async mounted() {
    var accessibilityFix = () => {
      let container = this.$refs['mapcontainer'],
        iframeMap = container.getElementsByTagName('iframe');
      if (iframeMap.length) {
        iframeMap[0].title = 'google maps iframe';
      }
      ['gm-fullscreen-control', 'gm-ui-hover-effect'].forEach(function (
        className
      ) {
        container.getElementsByClassName(className).forEach(function (elem) {
          elem.getElementsByTagName('img').forEach(function (imgTag) {
            if (!imgTag.alt) {
              imgTag.alt = 'UI Element';
            }
          });
        });
      });
    };
    var accessibilityInterval = setInterval(accessibilityFix, 1000);
    this.map = await this.$refs['map'].$mapPromise;
    this.map.addListener('click', this.mapClickHandler);
    this.map.addListener('tilesloaded', () => {
      this.ready = true;
      // Accessibility fix for google control img
      accessibilityFix();
      setTimeout(function () {
        clearInterval(accessibilityInterval);
      }, 5000);
      window.addEventListener('scroll', this.scrollHandler);
    });
    this.gMapsApi = VueGoogleMaps.gmapApi().maps;

    const markerRefs = Object.getOwnPropertyNames(this.$refs).filter(name =>
      name.match(/^marker/)
    );
    await markerRefs.asyncForEach(async name => {
      const marker = await this.$refs[name][0].$markerPromise;
      marker.addListener('mouseover', this.markerMouseoverHandler);
      marker.addListener('mouseout', this.markerMouseoutHandler);
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scrollHandler);
    this.observer.disconnect();
  }
};
</script>

<style lang="scss" scoped>
@import "~$scss/variables";

.LocationGoogleMap {
  .map-wrapper {
    position: relative;
    height: 630px;

    .map {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }

  // ANIMATION
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
  {
    opacity: 0;
  }

  // ---------

  /* The Modal (background) */
  .modal {
    display: none; /* Hidden by default */
    position: absolute; /* Stay in place */
    left: 0;
    top: 0;
    width: 100%; /* Full width */
    height: 100%; /* Full height */
    overflow: auto; /* Enable scroll if needed */
    background-color: $color-black; /* Fallback color */
    background-color: rgba(0, 0, 0, 0.7); /* Black w/ opacity */
  }

  /* The Close Button */
  .close {
    color: $color-white;
    font-size: 28px;
    position: absolute;
    font-weight: $bold;
    top: 11px;
    right: 21.5px;
  }

  .close:hover,
  .close:focus {
    color: $color-white;
    text-decoration: none;
    cursor: pointer;
  }

  // GOOGLE MAPS OVERRIDES FOR CUSTOMIZATIONS

  /* Cluster text positioning*/
  ::v-deep .cluster {
    div {
      top: -6px !important;
      left: -5px !important;
      font-weight: normal !important;
    }
  }

  /* Zoom buttons customizations */
  ::v-deep .gm-bundled-control {
    button {
      background-color: $color-blue-lochmara !important;
      color: $color-white !important;
      border: 2px solid $color-blue-2 !important;

      /*
         we use after and last-child to insert custom text on buttons
         the default plus and minus signs are images
       */

      &:after {
        content: "+";
        color: white;
        font-size: 31px;
        z-index: 3;
        position: absolute;
        top: -9px;
        right: 10px;
      }

      &:last-child:after {
        content: "\2013";
        color: white;
        font-size: 36px;
        z-index: 3;
        position: absolute;
        top: -11px;
        right: 10px;
      }

      /* Hides default plus and minus signs */
      img {
        display: none;
      }

      /* overlap borders */
      &:last-child {
        top: -3px !important;
      }
    }

    position: absolute !important;
    width: 40px !important;
    height: 78px !important;
    top: 5px !important;
    right: 0 !important;
    font-size: 28px !important;
    overflow: hidden !important;
    @media (min-width: $bootstrap-md) {
      right: 24px !important;
      top: 39px !important;
    }
  }

  /* Infobox styling and positioning */
  ::v-deep .gm-style .gm-style-iw-c {
    width: 250px !important;
    height: auto !important;
    max-height: 1000px !important;
    padding: 0 !important;
    border-radius: 0 !important;
    margin-left: -300px !important;
    transform: translate(
        -30%,
        -50%
    ) !important; // better then top/margins to center infobox
  }

  /* infobox inner container */
  ::v-deep .gm-style .gm-style-iw-d {
    max-height: 1000px !important;
    width: 109% !important; // hides the default white bar on the right side
  }

  /* infobox default after */
  ::v-deep .gm-style .gm-style-iw-t::after {
    background: rgba(
        255,
        255,
        255,
        0
    ) !important; // hides unwanted "comicbook" style
  }

  /* cluster  */
  ::v-deep .gm-style .gm-style-iw-t::after {
    box-shadow: none !important; // hides unwanted cluster shadow
  }

  ::v-deep button.gm-ui-hover-effect {
    display: none !important;
  }
}
</style>
